import type { ReactNode } from "react";
import { alpha, Box, Paper, Typography, useTheme } from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

export interface EmptyStateProps {
  /** Hero icon; inherits the tinted-primary circle color. */
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Caller-supplied CTA button(s). */
  action?: ReactNode;
  minHeight?: number | string;
  /** "panel" (default) = dashed-border Paper; "plain" = bare centered stack. */
  variant?: "panel" | "plain";
}

/** The app's empty-state archetype: tinted icon circle, title, hint, CTA. */
export function EmptyState({
  icon,
  title,
  description,
  action,
  minHeight = 400,
  variant = "panel",
}: EmptyStateProps) {
  const theme = useTheme();

  const content = (
    <>
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: alpha(theme.palette.primary.main, 0.6),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 3,
          "& svg": { fontSize: 50 },
        }}
      >
        {icon ?? <FolderOpenIcon />}
      </Box>
      <Typography variant="h5" fontWeight={600} color="text.primary" gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body1"
          color="text.secondary"
          component="div"
          sx={{ maxWidth: 450, mx: "auto", mb: action ? 4 : 0 }}
        >
          {description}
        </Typography>
      )}
      {action}
    </>
  );

  if (variant === "plain") {
    return (
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: { xs: 4, md: 6 },
        textAlign: "center",
        bgcolor: alpha(theme.palette.background.paper, 0.5),
        borderRadius: 3,
        border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight,
      }}
      elevation={0}
    >
      {content}
    </Paper>
  );
}
